
import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { FavouriteDefinition } from 'app/types-shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators as caseListingActionCreators } from 'app/area/case-report/case-listing/actions';
import { AddInstance, ClearInstance } from 'app/area/bowtie/action-types';
import { AppDispatch } from 'app/infrastructure/configure-store';
import debounce from 'lodash/debounce';
import { debounceExecuteFavouriteWait } from 'app/constants';

/**
 * Registers a bowtie instance in the bowtie slice as soon as the bowtie listing mounts.
 * This ensures only bowtie favourites are added to the bowtie slice, and that they are
 * present early enough for completion tracking (e.g. dashboard printing) instead of only
 * when the bowtie canvas eventually loads.
 */
export function useInstanceSlice() {
  const instanceKey = useInstanceKey();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    dispatch(new AddInstance(instanceKey));
    return () => {
      dispatch(new ClearInstance(instanceKey));
    };
  }, [instanceKey]);
}

export function useLoadPage(
  workingDefinition: FavouriteDefinition
) {

  const instanceKey = useInstanceKey();
  
  const {
    executionCounter, 
    // To override the API for the report in the dashboard
    reportApiOverrides 
  } = useInstanceOptions();

  const {
    executeFavouriteDefinition,
    clearResults
  } = useActionCreators();

  const debouncedExecute = useCallback(debounce((definition, startIndex, maxResults) => {
    executeFavouriteDefinition({
      definition,
      loadPermissions: true,
      startIndex,
      maxResults,
      replaceResult: true,
      overrideExecuteFavourite: reportApiOverrides?.executeFavourite
    });
  }, debounceExecuteFavouriteWait), [instanceKey]);

  const execute = useCallback((currentPage: number, maxOptionsPerPage: number) => {
    const startIndex = (currentPage - 1) * maxOptionsPerPage;
    debouncedExecute(workingDefinition, startIndex, maxOptionsPerPage);
  }, [workingDefinition, debouncedExecute, instanceKey]);

  const loadPage = useCallback((currentPage: number, maxOptionsPerPage: number) => {
    execute(currentPage, maxOptionsPerPage);
  }, [execute, executionCounter]);

  return {
    loadPage,
    clearResults
  };
}

export function usePagination(
  totalNumberOfResults: number,
  maxOptionsPerPage: number,
  minimumOptionsNeededBeforePagination: number
) {
  const [currentPage, setCurrentPage] = useState(1);
  const numberOfPages = !!totalNumberOfResults ? Math.ceil(totalNumberOfResults / maxOptionsPerPage) : 1;
  const shouldPaginate = numberOfPages > 1 && totalNumberOfResults > minimumOptionsNeededBeforePagination;
  const startIndex = (currentPage - 1) * maxOptionsPerPage;

  const first = useCallback(() => { setCurrentPage(1) }, []);
  const previous = useCallback(() => { setCurrentPage(currentPage - 1) }, [currentPage]);
  const next = useCallback(() => { setCurrentPage(currentPage + 1) }, [currentPage]);
  const last = useCallback(() => { setCurrentPage(numberOfPages) }, [numberOfPages]);

  useEffect(() => {
    if (currentPage > numberOfPages) {
      setCurrentPage(1);
    }
  }, [currentPage, numberOfPages]);

  return {
    numberOfPages,
    shouldPaginate,
    currentPage,
    startIndex,
    first,
    previous,
    next,
    last
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(caseListingActionCreators(instanceKey), dispatch), [instanceKey]);
}
